/**
 * Compliance dashboard router (US-033.5).
 *
 * Org-side view of NIL Go submissions for athletes in the caller's
 * compliance org(s). The compliance officer can:
 *   - list — paginated, filterable view of every submission their org's
 *            athletes have sent (and what NIL Go said back)
 *   - get  — full read-only snapshot detail for one submission
 *   - exportCsv — same filter set, returns a CSV blob suitable for the
 *            records request workflow Mike Parrish described
 *
 * Authorization mirrors `eligibility.router.ts` — caller must be a
 * Member of at least one Organization in role ('owner' | 'admin' |
 * 'compliance'). Submissions are scoped to athletes who are Members of
 * the same org(s). Without the shared-org check a compliance officer at
 * school A could read a submission from school B's athlete.
 *
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { protectedProcedure } from '../middleware/auth';

const COMPLIANCE_ROLES = ['owner', 'admin', 'compliance'] as const;

// biome-ignore lint/suspicious/noExplicitAny: prisma client type
async function callerComplianceOrgIds(db: any, userId: string): Promise<string[]> {
  const members = await db.member.findMany({
    where: { userId, role: { in: COMPLIANCE_ROLES as unknown as string[] } },
    select: { organizationId: true },
  });
  return members.map((m: { organizationId: string }) => m.organizationId);
}

// biome-ignore lint/suspicious/noExplicitAny: prisma client type
async function callerVisibleAthleteIds(db: any, userId: string): Promise<string[]> {
  const orgIds = await callerComplianceOrgIds(db, userId);
  if (orgIds.length === 0) return [];
  const members = await db.member.findMany({
    where: { organizationId: { in: orgIds } },
    select: { userId: true },
  });
  return Array.from(new Set(members.map((m: { userId: string }) => m.userId)));
}

const submissionStatusEnum = z.enum([
  'prepared',
  'submitted',
  'under_review',
  'approved',
  'needs_revision',
  'rejected',
  'cancelled',
]);

const filtersSchema = z.object({
  status: submissionStatusEnum.optional(),
  athleteId: z.string().optional(),
  payorId: z.string().optional(),
  affiliation: z.enum(['unknown', 'associated', 'non_associated']).optional(),
  /** When true, only deals whose cash-equivalent total is >= $600. When
   * false, only deals under $600 (submitted via force=true). Omit for both. */
  overThreshold: z.boolean().optional(),
  /** ISO date strings (or full ISO datetime). Inclusive. */
  dateFrom: z.string().date().or(z.string().datetime()).optional(),
  dateTo: z.string().date().or(z.string().datetime()).optional(),
});

const listInputSchema = filtersSchema.extend({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(25),
});

const NIL_GO_THRESHOLD_USD = 600;

/**
 * Build the prisma `where` clause from the filter set + the caller's
 * org scope. Returns null when the caller has no compliance standing
 * (handler short-circuits with empty results).
 */
function buildWhere(
  filters: z.infer<typeof filtersSchema>,
  athleteIds: string[],
): Record<string, unknown> | null {
  if (athleteIds.length === 0) return null;
  const where: Record<string, unknown> = { athleteId: { in: athleteIds } };
  if (filters.status) where.status = filters.status;
  if (filters.athleteId) {
    if (!athleteIds.includes(filters.athleteId)) {
      // Filter is asking for an athlete outside scope — collapse to no rows.
      where.athleteId = '__none__';
      return where;
    }
    where.athleteId = filters.athleteId;
  }
  const dealWhere: Record<string, unknown> = {};
  if (filters.payorId) dealWhere.payorId = filters.payorId;
  if (filters.affiliation) dealWhere.payor = { affiliation: filters.affiliation };
  if (filters.overThreshold !== undefined) {
    dealWhere.amount = filters.overThreshold
      ? { gte: NIL_GO_THRESHOLD_USD }
      : { lt: NIL_GO_THRESHOLD_USD };
  }
  if (Object.keys(dealWhere).length > 0) {
    where.deal = dealWhere;
  }
  if (filters.dateFrom || filters.dateTo) {
    const range: Record<string, Date> = {};
    if (filters.dateFrom) range.gte = new Date(filters.dateFrom);
    if (filters.dateTo) range.lte = new Date(filters.dateTo);
    where.createdAt = range;
  }
  return where;
}

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export const complianceRouter = {
  /**
   * Filter snapshot — returns the same numbers the dashboard chips show
   * across the visible scope (total / by status / over-$600). Pulls in
   * the same `where` predicate as `list`, just without pagination, so
   * the dashboard counters can't disagree with the table below them.
   */
  submissionsStats: protectedProcedure.input(filtersSchema).handler(async ({ context, input }) => {
    const { db, session } = context;
    const athleteIds = await callerVisibleAthleteIds(db, session.user.id);
    const where = buildWhere(input, athleteIds);
    if (!where) {
      return {
        canView: false,
        total: 0,
        byStatus: {} as Record<string, number>,
        overThreshold: 0,
      };
    }
    const [total, grouped, over] = await Promise.all([
      db.nilGoSubmission.count({ where }),
      db.nilGoSubmission.groupBy({
        by: ['status'],
        where,
        _count: { _all: true },
      }),
      db.nilGoSubmission.count({
        where: { ...where, deal: { ...(where.deal as object | undefined), amount: { gte: NIL_GO_THRESHOLD_USD } } },
      }),
    ]);
    return {
      canView: true,
      total,
      byStatus: Object.fromEntries(
        grouped.map((g: { status: string; _count: { _all: number } }) => [g.status, g._count._all]),
      ) as Record<string, number>,
      overThreshold: over,
    };
  }),

  /**
   * Paginated dashboard view. Returns submission rows + the joined deal
   * + payor + athlete fields the UI shows in the table (name, brand,
   * amount, signedDate, status). Snapshots are NOT inlined — the detail
   * view fetches the payloadSnapshot on demand to keep the list payload
   * small.
   */
  submissions: protectedProcedure.input(listInputSchema).handler(async ({ context, input }) => {
    const { db, session } = context;
    const { page, limit, ...filters } = input;
    const athleteIds = await callerVisibleAthleteIds(db, session.user.id);
    const where = buildWhere(filters, athleteIds);
    if (!where) {
      return { canView: false, rows: [], total: 0, page, totalPages: 0 };
    }

    const [rows, total] = await Promise.all([
      db.nilGoSubmission.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        skip: (page - 1) * limit,
        take: limit,
        include: {
          deal: {
            select: {
              id: true,
              title: true,
              brandName: true,
              amount: true,
              currency: true,
              signedDate: true,
              startDate: true,
              endDate: true,
              payor: {
                select: { id: true, legalName: true, affiliation: true, validationStatus: true },
              },
            },
          },
          athlete: { select: { id: true, name: true, email: true, school: true, sport: true } },
        },
      }),
      db.nilGoSubmission.count({ where }),
    ]);

    return {
      canView: true,
      rows: rows.map((r: any) => ({
        id: r.id,
        status: r.status,
        revisionNumber: r.revisionNumber,
        provider: r.provider,
        submittedAt: r.submittedAt?.toISOString() ?? null,
        decidedAt: r.decidedAt?.toISOString() ?? null,
        decisionReason: r.decisionReason,
        createdAt: r.createdAt.toISOString(),
        deal: {
          id: r.deal.id,
          title: r.deal.title,
          brandName: r.deal.brandName,
          amount: Number(r.deal.amount),
          currency: r.deal.currency,
          signedDate: r.deal.signedDate?.toISOString() ?? null,
          startDate: r.deal.startDate?.toISOString() ?? null,
          endDate: r.deal.endDate?.toISOString() ?? null,
          payor: r.deal.payor
            ? {
                id: r.deal.payor.id,
                legalName: r.deal.payor.legalName,
                affiliation: r.deal.payor.affiliation,
                validationStatus: r.deal.payor.validationStatus,
              }
            : null,
        },
        athlete: {
          id: r.athlete.id,
          name: r.athlete.name,
          email: r.athlete.email,
          school: r.athlete.school,
          sport: r.athlete.sport,
        },
      })),
      total,
      page,
      totalPages: Math.ceil(total / limit),
    };
  }),

  /** Read-only snapshot detail. Returns the full immutable payloadSnapshot
   * + decision metadata. Org-scoped via the same athleteIds check. */
  submissionDetail: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const athleteIds = await callerVisibleAthleteIds(db, session.user.id);
      if (athleteIds.length === 0) {
        throw new ORPCError('FORBIDDEN', { message: 'You are not a compliance member of any org.' });
      }
      const row = await db.nilGoSubmission.findFirst({
        where: { id: input.id, athleteId: { in: athleteIds } },
        include: {
          deal: { select: { id: true, title: true, brandName: true } },
          athlete: { select: { id: true, name: true, email: true, school: true, sport: true } },
        },
      });
      if (!row) throw new ORPCError('NOT_FOUND', { message: 'Submission not found' });

      // payloadSnapshot is stored as a JSON string per the schema. Parse
      // before returning so the UI doesn't need to JSON.parse client-side
      // (and so the boundary catches malformed snapshots).
      let parsedSnapshot: unknown = null;
      try {
        parsedSnapshot = row.payloadSnapshot ? JSON.parse(row.payloadSnapshot) : null;
      } catch {
        parsedSnapshot = { __error: 'payloadSnapshot is not valid JSON', raw: row.payloadSnapshot };
      }

      return {
        id: row.id,
        status: row.status,
        revisionNumber: row.revisionNumber,
        provider: row.provider,
        externalSubmissionId: row.externalSubmissionId,
        submittedAt: row.submittedAt?.toISOString() ?? null,
        decidedAt: row.decidedAt?.toISOString() ?? null,
        decisionReason: row.decisionReason,
        auditLog: row.auditLog,
        createdAt: row.createdAt.toISOString(),
        updatedAt: row.updatedAt.toISOString(),
        payloadSnapshot: parsedSnapshot,
        deal: row.deal,
        athlete: row.athlete,
      };
    }),

  /**
   * Record NIL Go's decision on a submission from the compliance side
   * (US-033.5 v2). The `export_only` provider has no callback — someone
   * has to type in what NIL Go's UI showed. US-033.4 lets the athlete do
   * it; this mutation lets the compliance officer record it for the
   * athlete (org's records request workflow).
   *
   * Transition rules (mirror US-033.4 to keep both write paths
   * symmetric, so when both ship the row state is consistent):
   *   - `approved` is terminal — re-recording succeeds idempotently
   *     when the new status matches; otherwise BAD_REQUEST.
   *   - Other statuses (`submitted` / `under_review` /
   *     `needs_revision` / `rejected`) are mutable; the row gets the
   *     new status + decidedAt + decisionReason and an audit log.
   *
   * Authorization: caller must share a compliance org with the
   * submission's athlete. The row-level User/NilGoSubmission policies
   * deny cross-user writes by design — we bypass via rawPrisma, same
   * pattern as the eligibility verify + organization self-serve flows
   * (see `eligibility.router.ts` and `afed847`).
   */
  recordDecision: protectedProcedure
    .input(
      z.object({
        submissionId: z.string(),
        status: z.enum(['approved', 'needs_revision', 'rejected', 'under_review']),
        reason: z.string().max(2000).optional(),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const callerId = session.user.id;
      const athleteIds = await callerVisibleAthleteIds(db, callerId);
      if (athleteIds.length === 0) {
        throw new ORPCError('FORBIDDEN', { message: 'You are not a compliance member of any org.' });
      }
      const row = await db.nilGoSubmission.findFirst({
        where: { id: input.submissionId, athleteId: { in: athleteIds } },
        select: {
          id: true,
          status: true,
          athleteId: true,
          dealId: true,
          revisionNumber: true,
          athlete: { select: { email: true, name: true } },
          deal: { select: { title: true, brandName: true } },
        },
      });
      if (!row) throw new ORPCError('NOT_FOUND', { message: 'Submission not found' });

      if (row.status === 'approved') {
        if (input.status === 'approved') {
          // Idempotent re-record — no-op, surface success.
          return { ok: true, status: 'already_approved' as const };
        }
        throw new ORPCError('BAD_REQUEST', {
          message: 'Approved submissions are immutable. Resubmit the deal to create a new revision.',
        });
      }
      if (row.status === 'cancelled') {
        throw new ORPCError('BAD_REQUEST', {
          message: 'Submission was cancelled — record the decision on a fresh resubmission instead.',
        });
      }
      if ((input.status === 'needs_revision' || input.status === 'rejected') && !input.reason?.trim()) {
        throw new ORPCError('BAD_REQUEST', {
          message: 'Add the reason NIL Go gave so the athlete can act on it.',
        });
      }

      const { prisma: rawPrisma } = await import('@app/db');
      const now = new Date();
      await rawPrisma.nilGoSubmission.update({
        where: { id: row.id },
        data: {
          status: input.status,
          decidedAt: input.status === 'under_review' ? null : now,
          decisionReason: input.reason?.trim() || null,
        },
      });

      // Notify the athlete: in-app row + email. Compliance officers run
      // this after recording NIL Go's verdict offline, so the athlete
      // may not be on the page when it lands. The in-app row drives the
      // bell badge; the email is what actually reaches them while they're
      // off NilBoard.
      const { createId } = await import('@app/db');
      const dealLabel = row.deal.title || row.deal.brandName || 'your deal';
      const title =
        input.status === 'approved'
          ? `NIL Go approved your submission for ${dealLabel}`
          : input.status === 'needs_revision'
            ? `NIL Go asked for revisions on ${dealLabel}`
            : input.status === 'rejected'
              ? `NIL Go rejected your submission for ${dealLabel}`
              : `NIL Go marked your submission under review (${dealLabel})`;
      // The body MUST lead with the reason. The notification bell clamps
      // the body to a few lines, so any preamble pushes the actual reason
      // out of view — the athlete saw the label "Reason from NIL Go:" with
      // nothing under it. The title already states what happened and on
      // which deal, so the body's first line is the substance.
      const reasonLine = input.reason?.trim() ?? '';
      const nextStep =
        input.status === 'approved'
          ? 'No further action needed — your submission is approved.'
          : input.status === 'needs_revision'
            ? 'Next step: open the deal, address this, and resubmit to NIL Go.'
            : input.status === 'rejected'
              ? "Next step: review this with your compliance contact. You can resubmit once it's resolved."
              : "Next step: NIL Go is reviewing — you'll get another notification when the decision lands.";
      const body = [
        reasonLine,
        nextStep,
        `(Revision #${row.revisionNumber}, recorded by your compliance team.)`,
      ]
        .filter(Boolean)
        .join('\n\n');
      const actionUrl = `/deals/${row.dealId}`;

      await rawPrisma.notification.create({
        data: {
          id: createId(),
          userId: row.athleteId,
          type: 'system',
          title,
          body,
          actionUrl,
        },
      });

      // Email delivery is best-effort — if Resend is down or unconfigured
      // the in-app row already landed, and the next sign-in surfaces it
      // via the bell. Catch + log so a transient email failure doesn't
      // 500 the whole mutation.
      //
      // Goes through `@app/notifications.sendNotification` (already a
      // declared dep) so we don't import `@app/email` directly from
      // here — Rollup can't resolve it cross-package without that dep
      // being on `@app/api`'s package.json, which was the build break.
      if (row.athlete.email) {
        try {
          const { sendNotification } = await import('@app/notifications');
          const appUrl = process.env.BETTER_AUTH_URL ?? process.env.VITE_APP_URL ?? 'https://www.nilboard.app';
          const linkUrl = `${appUrl.replace(/\/$/, '')}${actionUrl}`;
          await sendNotification({
            userId: row.athleteId,
            title,
            body: `${body}\n\nOpen the deal: ${linkUrl}`,
            channels: ['email'],
            email: row.athlete.email,
          });
        } catch (e) {
          console.error('[compliance.recordDecision] email send failed:', e);
        }
      }

      return { ok: true, status: 'recorded' as const, decidedAt: now.toISOString() };
    }),

  /** CSV export of the filtered view. Returns a string the UI can download
   * via a Blob. We cap at 5000 rows so a runaway filter can't take down
   * the function — anything larger needs the records-request workflow. */
  submissionsExportCsv: protectedProcedure
    .input(filtersSchema)
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const athleteIds = await callerVisibleAthleteIds(db, session.user.id);
      const where = buildWhere(input, athleteIds);
      if (!where) {
        throw new ORPCError('FORBIDDEN', { message: 'You are not a compliance member of any org.' });
      }
      const rows = await db.nilGoSubmission.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        take: 5000,
        include: {
          deal: { select: { title: true, brandName: true, amount: true, currency: true, signedDate: true, payor: { select: { legalName: true, affiliation: true, validationStatus: true } } } },
          athlete: { select: { name: true, email: true, school: true, sport: true, ncaaId: true } },
        },
      });

      const headers = [
        'submission_id',
        'submitted_at',
        'status',
        'revision_number',
        'provider',
        'decision_reason',
        'athlete_name',
        'athlete_email',
        'athlete_school',
        'athlete_sport',
        'athlete_ncaa_id',
        'deal_title',
        'deal_brand_name',
        'deal_signed_date',
        'deal_amount',
        'deal_currency',
        'payor_legal_name',
        'payor_affiliation',
        'payor_validation_status',
      ];

      const body = rows.map((r: any) =>
        [
          r.id,
          r.submittedAt?.toISOString() ?? '',
          r.status,
          r.revisionNumber,
          r.provider,
          r.decisionReason ?? '',
          r.athlete.name,
          r.athlete.email,
          r.athlete.school ?? '',
          r.athlete.sport ?? '',
          r.athlete.ncaaId ?? '',
          r.deal.title,
          r.deal.brandName,
          r.deal.signedDate?.toISOString().slice(0, 10) ?? '',
          Number(r.deal.amount).toFixed(2),
          r.deal.currency,
          r.deal.payor?.legalName ?? '',
          r.deal.payor?.affiliation ?? '',
          r.deal.payor?.validationStatus ?? '',
        ]
          .map(csvEscape)
          .join(','),
      );

      const csv = `${headers.join(',')}\n${body.join('\n')}\n`;
      return { csv, rowCount: rows.length, truncated: rows.length === 5000 };
    }),

  /** Distinct athletes/payors in scope — populates the filter dropdowns
   * on the dashboard without round-trip queries per type-ahead. */
  filterOptions: protectedProcedure.handler(async ({ context }) => {
    const { db, session } = context;
    const athleteIds = await callerVisibleAthleteIds(db, session.user.id);
    if (athleteIds.length === 0) {
      return { canView: false, athletes: [], payors: [] };
    }
    const [athletes, deals] = await Promise.all([
      db.user.findMany({
        where: { id: { in: athleteIds } },
        select: { id: true, name: true, email: true },
        orderBy: { name: 'asc' },
      }),
      // Distinct payor ids that have at least one submission in scope.
      db.deal.findMany({
        where: {
          athleteId: { in: athleteIds },
          nilGoSubmissions: { some: {} },
          payorId: { not: null },
        },
        select: { payor: { select: { id: true, legalName: true } } },
        distinct: ['payorId'],
      }),
    ]);
    const payorMap = new Map<string, string>();
    for (const d of deals) {
      if (d.payor) payorMap.set(d.payor.id, d.payor.legalName);
    }
    return {
      canView: true,
      athletes: athletes.map((a: { id: string; name: string; email: string }) => ({
        id: a.id,
        name: a.name,
        email: a.email,
      })),
      payors: [...payorMap.entries()].map(([id, legalName]) => ({ id, legalName })),
    };
  }),
};
